import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  useDriverLoad,
  useDepartLoad,
  useArriveLoad,
  useDeliverLoad,
  apiLoadToDriverLoad,
  getChecklistForLoad,
  type ChecklistItem,
  type DriverLoad,
} from "../data/driverLoadData";
import { apiClient, getApiErrorMessage } from "../api/client";
import { useLocale } from "../contexts/LocaleContext";
import { useTenantTimeZone } from "../hooks/useTenantTimeZone";
import CancelledLoadNotice from "../components/trucking/CancelledLoadNotice";
import { formatInstantDay, formatInstantTime } from "../lib/calendar-date";
import { toDatetimeLocal, fromDatetimeLocal } from "../lib/datetime-local";
import { Button } from "../components/ui/button";
import { CheckCircle2, Circle, ArrowLeft, AlertTriangle, MapPin, Fuel, Flag, Check, Loader2, X, Clock } from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { useBack } from '../hooks/useBack';
import { useMyHos } from '../hooks/useMyHos';
import { fmtHours } from '../lib/display-labels';

/* -- Bottom Sheet helper -- */
const BottomSheet = ({ open, onClose, title, children }: {
  open: boolean; onClose: () => void; title: string; children: React.ReactNode;
}) => {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center"
      style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="animate-slide-up w-full max-w-lg bg-card rounded-t-2xl border-t border-border"
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-4 pb-3 border-b border-border">
          <h3 className="text-sm font-bold text-foreground">{title}</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-secondary transition-colors">
            <X className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>
        <div className="px-5 py-4 max-h-[70vh] overflow-y-auto">
          {children}
        </div>
      </div>
    </div>
  );
};

type Screen = "details" | "checklist" | "enroute" | "arrival" | "condition" | "signature" | "closed";

// No "Notification" step: it drew a mock phone lock screen saying "New load
// assigned" with a Decline button that does nothing (its route is gone), and
// every load opened on it, an in-progress one included (fix/driver-app-trucking).
const preArrivalTabs: { key: Screen; label: string }[] = [
  { key: "details", label: "1. Load received" },
  { key: "checklist", label: "2. Pre-departure" },
  { key: "enroute", label: "3. En route" },
];

/**
 * The step a load opens on is the one its status is at. Every load opened on
 * the notification mock, so a driver tapping a dispatched load from Today saw
 * "New load assigned" and had to page forward to where they were.
 * `delivered` is work done awaiting the receiver's signature; `signed` is
 * closed. An unknown status opens on the load's details, never a later step.
 */
export function openingStep(status: string | null | undefined): { screen: Screen; arrival: boolean } {
  switch (status) {
    case "en_route":
      return { screen: "enroute", arrival: false };
    case "arrived":
      return { screen: "arrival", arrival: true };
    case "delivered":
      return { screen: "signature", arrival: true };
    case "signed":
    case "completed":
      return { screen: "closed", arrival: true };
    default:
      return { screen: "details", arrival: false };
  }
}

const arrivalTabs: { key: Screen; label: string }[] = [
  { key: "arrival", label: "1. Arrive at site" },
  { key: "condition", label: "2. Condition check" },
  { key: "signature", label: "3. Receiver signature" },
  { key: "closed", label: "4. Load closed" },
];

type ConditionStatus = "none" | "ok" | "damaged";

interface CargoCondition {
  item: string;
  weight: string;
  status: ConditionStatus;
  damageNote: string;
}

/**
 * Hours of service: driving hours left in the 7-day cycle, from GET
 * /trucking/drivers/me/hos (#815).
 *
 * The chip read `HOS: 0h` from a mapper that hard-coded 0, then `3.8h` and
 * "0 minus 1.4" on later steps, all literals. Since the API fix a driver with
 * no logs in the window reads null, which is a dash; a real 70 means a fresh
 * cycle. Neutral, not green: a figure here is not a clean bill.
 */
const HosChip = () => {
  const { data } = useMyHos();
  const left = data?.driving_remaining ?? null;
  const label = left == null ? 'Hours of service: not recorded' : `Hours of service: ${fmtHours(left, 1)} hours of driving left in the 7-day cycle`;
  return (
    <div className="flex items-center gap-1.5 rounded-full border border-border px-2.5 py-1" aria-label={label}>
      <span className="text-[11px] font-semibold text-muted-foreground" aria-hidden="true">
        {left == null ? 'HOS: —' : `HOS: ${fmtHours(left, 1)}h left`}
      </span>
    </div>
  );
};

const DriverLoadView = () => {
  const navigate = useNavigate();
  // The driver's home is /my-work/today. "/field" is not a route in the /my-work
  // tree, so it fell through to the catch-all (#815).
  const goBack = useBack("/my-work/today");
  const { loadId } = useParams();
  const { isMetric } = useLocale();
  const distanceUnit = isMetric ? 'km' : 'mi';
  const [screen, setScreen] = useState<Screen>("details");
  // Set once, from the first load read; later refetches never move the driver.
  const openedRef = useRef(false);
  const [load, setLoad] = useState<DriverLoad | null>(null);
  const [checklist, setChecklist] = useState<ChecklistItem[]>([]);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [arrivalTime, setArrivalTime] = useState<string | null>(null);
  // The arrival INSTANT, so the wait-time start can prefill from it on the
  // tenant's clock rather than re-combining "HH:MM" with the device's today.
  const [arrivedAtIso, setArrivedAtIso] = useState<string | null>(null);
  // Drive time is the route's `drive_time_minutes`, or a dash. It was a
  // hard-coded "3h 14min" on the arrival and closed screens, printed for every
  // load whatever was driven (fix/driver-app-trucking).
  const [cargoConditions, setCargoConditions] = useState<CargoCondition[]>([]);
  const [receiverName, setReceiverName] = useState("");
  const [receiverPosition, setReceiverPosition] = useState("");
  const [signatureData, setSignatureData] = useState<string | null>(null);
  const [signedAt, setSignedAt] = useState<string | null>(null);
  const [isArrivalPhase, setIsArrivalPhase] = useState(false);
  const [odometerAtDeparture, setOdometerAtDeparture] = useState("");
  const [odometerAtDelivery, setOdometerAtDelivery] = useState("");

  // Fuel stop modal
  const [showFuelModal, setShowFuelModal] = useState(false);
  const [fuelLitres, setFuelLitres] = useState("");
  const [fuelPricePerLitre, setFuelPricePerLitre] = useState("");
  const [fuelProvince, setFuelProvince] = useState("AB");
  const [fuelStation, setFuelStation] = useState("");
  const [fuelOdometer, setFuelOdometer] = useState("");
  const [fuelSubmitting, setFuelSubmitting] = useState(false);

  // Flag delay modal
  const [showDelayModal, setShowDelayModal] = useState(false);
  const [delayReason, setDelayReason] = useState("");
  const [delayNotes, setDelayNotes] = useState("");
  const [delayMinutes, setDelayMinutes] = useState<number>(15);
  const [delaySubmitting, setDelaySubmitting] = useState(false);

  // Wait time modal
  const [showWaitModal, setShowWaitModal] = useState(false);
  const [waitStart, setWaitStart] = useState("");
  const [waitEnd, setWaitEnd] = useState("");
  const [waitReason, setWaitReason] = useState("");
  const [waitNotes, setWaitNotes] = useState("");
  const [waitSubmitting, setWaitSubmitting] = useState(false);
  const [loggedWaitMinutes, setLoggedWaitMinutes] = useState<number | null>(null);
  const [waitTimeMinutes, setWaitTimeMinutes] = useState(0);

  // API hooks
  const { data: apiData, isLoading: isLoadingData } = useDriverLoad(loadId || "");
  /*
    `useAcceptLoad` and `useDeclineLoad` are gone from this screen. Both hit
    `PATCH /trucking/loads/:id/accept` / `/decline`, and both routes are dead:
    they write `status = 'accepted'` / `'declined'` — not labels on the
    `load_status` enum — into `accepted_at` / `declined_at` columns that do not
    exist on `loads`. Verified against production 2026-09-04. Every call 500s,
    so a driver accepting a load has never worked on any tenant.

    There is no working substitute. The status route refuses the same two words
    (API PR #573), and the transition routes that DO work — /depart, /arrive,
    /deliver — record something else entirely. Disabled with honest copy until
    the API can hold the state; do not re-wire these to another endpoint.
  */
  const departLoad = useDepartLoad();
  const arriveLoad = useArriveLoad();
  const deliverLoad = useDeliverLoad();

  // Populate load from API data + fetch move job progress
  useEffect(() => {
    if (apiData) {
      const transformed = apiLoadToDriverLoad(apiData);
      setLoad(transformed);
      if (!openedRef.current) {
        openedRef.current = true;
        const step = openingStep(apiData?.status ?? apiData?.load?.status);
        setScreen(step.screen);
        setIsArrivalPhase(step.arrival);
      }

      if (transformed.moveJobId) {
        apiClient
          .get(`/trucking/move-jobs/${transformed.moveJobId}`)
          .then((res) => {
            const mj = res.data?.data ?? res.data;
            // The counts live on the master BOL (`masterBol`), not on the move
            // job row: this read `mj.total_loads ?? 0`, keys the route never
            // sends, so it could only ever say "0 of 0".
            const mb = mj?.masterBol ?? null;
            const count = (v: unknown) => (typeof v === "number" && Number.isFinite(v) ? v : null);
            setLoad((prev) =>
              prev
                ? {
                    ...prev,
                    moveJobProgress: mb
                      ? {
                          total: count(mb.total_loads),
                          delivered: count(mb.loads_delivered),
                          signed: count(mb.loads_signed),
                          status: typeof mb.status === "string" ? mb.status : null,
                        }
                      : null,
                  }
                : prev
            );
          })
          .catch(() => {
            /* move-job fetch failed — degrade gracefully */
          });
      }
    }
  }, [apiData]);

  useEffect(() => {
    if (load) {
      setChecklist(getChecklistForLoad(load));
    }
  }, [load]);

  useEffect(() => {
    const t = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  // The clock and date the driver sees are the TENANT's (companies.time_zone),
  // not the phone's — a phone left on another zone after a trip must not tell
  // the driver, or the receiver signing beside them, a different day. No zone
  // reads "—" rather than a guessed clock.
  const timeZone = useTenantTimeZone();
  const currentIso = currentTime.toISOString();
  const timeStr = formatInstantTime(currentIso, timeZone, { hour: "2-digit", minute: "2-digit", hour12: false }) ?? "—";
  const dateStr = formatInstantDay(currentIso, timeZone, { weekday: "long", month: "long", day: "numeric" }) ?? "—";

  useEffect(() => {
    if (load) {
      setCargoConditions(
        load.cargo.map((c) => ({ item: c.item, weight: c.weight, status: "none" as ConditionStatus, damageNote: "" }))
      );
    }
  }, [load?.cargo]);

  const toggleCheck = (id: string) => {
    setChecklist((prev) =>
      prev.map((item) => (item.id === id ? { ...item, checked: !item.checked } : item))
    );
  };

  const allChecked = checklist.length > 0 && checklist.every((c) => c.checked);
  const remainingChecks = checklist.filter((c) => !c.checked).length;

  /**
   * Advances the LOCAL step only, and the button says so. It makes no claim
   * about the server — the previous handler stamped `status: "accepted"` and
   * an `acceptedAt` into local state and toasted "Load accepted!" on top of a
   * request that had 500'd, which is the exact success-over-a-failure shape
   * this pass exists to remove.
   *
   * Without it, disabling Accept would strand the driver here and take the
   * checklist, Depart, Arrive and the BOL signature down with it.
   */
  const handleStartChecks = () => setScreen("checklist");

  const handleDepart = async () => {
    if (!allChecked || !loadId) return;
    const odo = parseFloat(odometerAtDeparture);
    if (!odometerAtDeparture || isNaN(odo)) {
      toast.error("Enter odometer reading before departing");
      return;
    }
    try {
      await departLoad.mutateAsync({ loadId, odometer_at_departure: odo });
      setLoad((prev) => prev ? { ...prev, status: "en_route" } : prev);
      setScreen("enroute");
      toast.success("Departed — safe travels!");
    } catch (err) {
      // LOAD_RATES_LOCKED on a signed BOL, among others: as the API words it.
      toast.error(getApiErrorMessage(err, "Failed to record departure"));
    }
  };

  const handleArrival = async () => {
    if (!loadId) return;
    const now = new Date();
    const arrTime = formatInstantTime(now.toISOString(), timeZone, { hour: "2-digit", minute: "2-digit", hour12: false });
    try {
      await arriveLoad.mutateAsync({ loadId, arrived_at: now.toISOString() });
      setArrivalTime(arrTime);
      setArrivedAtIso(now.toISOString());
      setLoad((prev) => prev ? { ...prev, status: "arrived" } : prev);
      setIsArrivalPhase(true);
      setScreen("arrival");
      toast.success("Arrival confirmed!");
    } catch {
      toast.error("Failed to confirm arrival");
    }
  };

  const setConditionStatus = (index: number, status: ConditionStatus) => {
    setCargoConditions((prev) =>
      prev.map((c, i) => (i === index ? { ...c, status, damageNote: status === "ok" ? "" : c.damageNote } : c))
    );
  };

  const setDamageNote = (index: number, note: string) => {
    setCargoConditions((prev) =>
      prev.map((c, i) => (i === index ? { ...c, damageNote: note } : c))
    );
  };

  const allConditionsMarked = cargoConditions.length > 0 && cargoConditions.every((c) => c.status !== "none");
  const hasDamage = cargoConditions.some((c) => c.status === "damaged");

  const handleConditionContinue = () => {
    if (!allConditionsMarked) return;
    setScreen("signature");
  };

  const handleSignSubmit = async () => {
    if (!receiverName.trim() || !signatureData || !loadId) return;
    const odo = parseFloat(odometerAtDelivery);
    if (!odometerAtDelivery || isNaN(odo)) {
      toast.error("Enter odometer reading at delivery");
      return;
    }
    const damage_items = cargoConditions
      .filter((c) => c.status === "damaged")
      .map((c) => ({ item: c.item, note: c.damageNote }));

    try {
      await deliverLoad.mutateAsync({
        loadId,
        payload: {
          receiver_name: receiverName,
          receiver_position: receiverPosition,
          receiver_signature_data_url: signatureData,
          damage_items,
          odometer_at_delivery: odo,
        },
      });
      const now = new Date();
      setSignedAt(formatInstantTime(now.toISOString(), timeZone, { hour: "2-digit", minute: "2-digit", hour12: true }) ?? "—");
      setLoad((prev) => prev ? { ...prev, status: "completed" } : prev);
      setScreen("closed");
      toast.success("BOL signed — load closed!");
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Failed to submit delivery"));
    }
  };

  const handleFuelSubmit = async () => {
    const litres = parseFloat(fuelLitres);
    const price = parseFloat(fuelPricePerLitre);
    const odo = parseFloat(fuelOdometer);
    if (!litres || !price || !odo) { toast.error("Fill in litres, price, and odometer"); return; }
    setFuelSubmitting(true);
    try {
      await apiClient.post('/trucking/fuel-logs', {
        truck_id: (apiData as any)?.truck_id || null,
        driver_id: (apiData as any)?.driver_id || null,
        load_id: loadId,
        litres,
        price_per_litre: price,
        total_cost: Math.round(litres * price * 100) / 100,
        province: fuelProvince,
        station_name: fuelStation || null,
        odometer_km: odo,
        logged_at: new Date().toISOString(),
      });
      toast.success("Fuel stop logged");
      setShowFuelModal(false);
      setFuelLitres(""); setFuelPricePerLitre(""); setFuelProvince("AB"); setFuelStation(""); setFuelOdometer("");
    } catch {
      toast.error("Failed to log fuel stop");
    }
    setFuelSubmitting(false);
  };

  const handleDelaySubmit = async () => {
    if (!delayReason) { toast.error("Select a delay reason"); return; }
    setDelaySubmitting(true);
    try {
      await apiClient.patch(`/trucking/loads/${loadId}/flag-delay`, {
        reason: delayReason,
        notes: delayNotes || null,
        expected_delay_minutes: delayMinutes,
      });
      toast.success("Dispatcher notified of delay");
      setShowDelayModal(false);
      setDelayReason(""); setDelayNotes(""); setDelayMinutes(15);
    } catch {
      toast.error("Failed to flag delay");
    }
    setDelaySubmitting(false);
  };

  const handleWaitSubmit = async () => {
    if (!waitStart || !waitEnd) { toast.error("Set wait start and end times"); return; }
    // The pickers show the TENANT's wall clock, so they are read back on it and
    // sent as instants. The bare "YYYY-MM-DDTHH:MM" used to go to a timestamptz
    // column as-is, where the database read it in its own zone, not the
    // tenant's or the driver's.
    const waitStartIso = fromDatetimeLocal(waitStart, timeZone);
    const waitEndIso = fromDatetimeLocal(waitEnd, timeZone);
    if (!waitStartIso || !waitEndIso) { toast.error("Could not read the wait times in your company's time zone"); return; }
    setWaitSubmitting(true);
    try {
      const res = await apiClient.post(`/trucking/loads/${loadId}/wait-time`, {
        wait_start: waitStartIso,
        wait_end: waitEndIso,
        reason: waitReason || null,
        notes: waitNotes || null,
      });
      const data = res.data?.data ?? res.data;
      // Wait time is billable, and the server is what scores it. A missing
      // duration means "not scored", not "waited zero minutes" — and the
      // banner below is already written for that: loggedWaitMinutes is typed
      // number | null and its row renders only when the value is non-null.
      // `|| 0` was the one thing keeping that null from ever arriving, so a
      // driver whose wait the server could not compute was told
      // "Wait logged: 0 min — billing eligible".
      const rawMinutes = data?.duration_minutes;
      const parsed = Number(rawMinutes);
      const mins = rawMinutes === null || rawMinutes === undefined || rawMinutes === '' || !Number.isFinite(parsed)
        ? null
        : parsed;
      setLoggedWaitMinutes(mins);
      setWaitTimeMinutes((prev) => prev + (mins ?? 0));
      toast.success("Wait time logged");
      setShowWaitModal(false);
      setWaitReason(""); setWaitNotes("");
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Failed to log wait time"));
    }
    setWaitSubmitting(false);
  };

  const formatDuration = (start: string, end: string) => {
    if (!start || !end) return '--';
    const ms = new Date(end).getTime() - new Date(start).getTime();
    if (ms <= 0) return '0 min';
    const h = Math.floor(ms / 3600000);
    const m = Math.round((ms % 3600000) / 60000);
    return h > 0 ? `${h}h ${m}min` : `${m}min`;
  };

  const formatMinutes = (m: number) => {
    const h = Math.floor(m / 60);
    const min = Math.round(m % 60);
    return h > 0 ? `${h}h ${min}min` : `${min}min`;
  };

  const currentTabs = isArrivalPhase ? arrivalTabs : preArrivalTabs;

  // Loading state
  if (isLoadingData || !load) {
    return (
      <div className="min-h-full bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // A cancelled load is final (fe#815 cancel): no steps, no actions. It used
  // to open on its details with Depart live, and the API took it.
  const rawStatus = apiData?.status ?? apiData?.load?.status;
  if (rawStatus === "cancelled") {
    const raw = (apiData?.load ?? apiData) as Record<string, unknown> | undefined;
    return (
      <CancelledLoadNotice
        bolNumber={load.bolNumber}
        reason={typeof raw?.cancel_reason === "string" ? raw.cancel_reason : null}
        cancelledAt={typeof raw?.cancelled_at === "string" ? raw.cancelled_at : null}
        timeZone={timeZone}
        onBack={() => navigate(-1)}
      />
    );
  }

  // Rig-move progress, from the master BOL's counts (api#990 sends the move
  // job's name on the load). A count the API did not send is a dash, and a
  // move job with no master BOL says so; nothing is added up here.
  const count = (n: number | null | undefined) => (n == null ? "—" : String(n));
  const mp = load.moveJobProgress;
  const rigMoveProgressLine = mp === undefined
    ? "—"
    : mp === null
      ? "No master BOL yet"
      : `${count(mp.delivered)} of ${count(mp.total)} loads delivered`;
  const progressBar = (track: string, fill: string) =>
    mp && mp.total != null && mp.total > 0 && mp.delivered != null ? (
      <div className={`w-full h-1.5 rounded-full ${track} mt-1.5 overflow-hidden`}>
        <div className={`h-full rounded-full ${fill} transition-all`} style={{ width: `${Math.min(100, (mp.delivered / mp.total) * 100)}%` }} />
      </div>
    ) : null;

  // The way to the master BOL's operator signature (api#941), on the first
  // step and the closed one. Gated on `moveJobId` alone: GET /trucking/loads/:id
  // sends `move_job_id` (l.*) and no move job NAME, so the rig-move banners,
  // which need the name, never render.
  const masterBolLink = load.moveJobId ? (
    <div className="mx-4 mb-3">
      <button
        type="button"
        data-testid="master-bol-link"
        onClick={() => navigate(`/driver/move-jobs/${load.moveJobId}/master-bol`)}
        className="w-full rounded-lg border border-fieldiq-border px-3 py-2.5 text-left text-xs font-semibold text-fieldiq-text"
      >
        Part of a rig move · Master BOL &rarr;
      </button>
    </div>
  ) : null;

  /* -- Signature Canvas -- */
  const SignatureCanvas = ({ onSign }: { onSign: (data: string) => void }) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const isDrawing = useRef(false);
    const lastPos = useRef<{ x: number; y: number } | null>(null);

    const getPos = (e: React.TouchEvent | React.MouseEvent) => {
      const canvas = canvasRef.current;
      if (!canvas) return { x: 0, y: 0 };
      const rect = canvas.getBoundingClientRect();
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;
      if ("touches" in e) {
        return { x: (e.touches[0].clientX - rect.left) * scaleX, y: (e.touches[0].clientY - rect.top) * scaleY };
      }
      return { x: (e.clientX - rect.left) * scaleX, y: (e.clientY - rect.top) * scaleY };
    };

    const startDraw = (e: React.TouchEvent | React.MouseEvent) => {
      e.preventDefault();
      isDrawing.current = true;
      lastPos.current = getPos(e);
    };

    const draw = (e: React.TouchEvent | React.MouseEvent) => {
      e.preventDefault();
      if (!isDrawing.current || !lastPos.current) return;
      const canvas = canvasRef.current;
      const ctx = canvas?.getContext("2d");
      if (!ctx || !canvas) return;
      const pos = getPos(e);
      ctx.beginPath();
      ctx.moveTo(lastPos.current.x, lastPos.current.y);
      ctx.lineTo(pos.x, pos.y);
      ctx.strokeStyle = "#e5e7eb";
      ctx.lineWidth = 2.5;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.stroke();
      lastPos.current = pos;
    };

    const endDraw = () => {
      isDrawing.current = false;
      lastPos.current = null;
      const canvas = canvasRef.current;
      if (canvas) onSign(canvas.toDataURL());
    };

    const clearCanvas = () => {
      const canvas = canvasRef.current;
      const ctx = canvas?.getContext("2d");
      if (ctx && canvas) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        onSign("");
      }
    };

    return (
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">Receiver Signature</p>
          <button onClick={clearCanvas} className="text-[11px] font-medium text-muted-foreground hover:text-foreground transition-colors">
            Clear
          </button>
        </div>
        <canvas
          ref={canvasRef}
          width={540}
          height={200}
          className="w-full h-28 rounded-lg border border-border bg-card/60 cursor-crosshair touch-none"
          onMouseDown={startDraw}
          onMouseMove={draw}
          onMouseUp={endDraw}
          onMouseLeave={endDraw}
          onTouchStart={startDraw}
          onTouchMove={draw}
          onTouchEnd={endDraw}
        />
        {!signatureData && (
          <p className="text-[11px] text-muted-foreground text-center mt-1.5">Sign here with your finger</p>
        )}
      </div>
    );
  };

  /* -- Phone frame wrapper -- */
  const PhoneFrame = ({ children, statusBarTime }: { children: React.ReactNode; statusBarTime: string }) => (
    <div className="w-full max-w-sm mx-auto">
      <div className="bg-card rounded-2xl border border-border overflow-hidden shadow-xl">
        {/* Status bar */}
        <div className="px-5 pt-3 pb-1 flex items-center justify-between text-xs text-muted-foreground">
          <span className="font-mono font-medium">{statusBarTime}</span>
          <div className="flex items-center gap-1.5">
            <div className="w-4 h-2 rounded-sm border border-muted-foreground/50 relative">
              <div className="absolute inset-0.5 bg-success rounded-[1px]" style={{ width: "70%" }} />
            </div>
          </div>
        </div>
        {children}
      </div>
    </div>
  );

  /* -- App header helper -- */
  const AppHeader = () => (
    <div className="px-5 py-3 flex items-center justify-between">
      <div className="flex items-center gap-2">
        <div className="w-2 h-2 rounded-full bg-primary" />
        <span className="text-sm font-bold text-foreground">FieldIQ</span>
      </div>
      <HosChip />
    </div>
  );

  /* -- Bottom nav helper -- */
  const BottomNav = () => (
    <div className="border-t border-border px-6 py-2.5 flex items-center justify-around">
      {["Loads", "BOLs", "Dispatch", "Profile"].map((label) => (
        <span key={label} className={`text-[10px] font-medium ${label === "Loads" ? "text-primary" : "text-muted-foreground"}`}>
          {label}
        </span>
      ))}
    </div>
  );

  /* ======================================================
     PRE-ARRIVAL SCREENS (1-4)
     ====================================================== */

  /* -- Screen 2: Load Details -- */
  const DetailsScreen = () => (
    <PhoneFrame statusBarTime={timeStr}>
      <AppHeader />

      {/* Title */}
      <div className="px-5 pb-3">
        <h1 className="text-lg font-bold text-foreground">New load{load.driverName ? `, ${load.driverName}` : ""}</h1>
        <p className="text-xs text-muted-foreground">Unit {load.truckId || "—"} · {load.loadType} · {dateStr.split(",").pop()?.trim()}</p>
      </div>

      {/* Load card */}
      <div className="mx-4 mb-4 bg-secondary rounded-xl border border-border overflow-hidden">
        {/* Header bar */}
        <div className="px-4 py-2.5 flex items-center justify-between border-b border-border bg-primary/10">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-primary" />
            <span className="text-xs font-semibold text-primary">New load assigned</span>
          </div>
          <span className="text-[11px] font-mono text-primary">BOL #{load.bolNumber}</span>
        </div>

        {/* Route */}
        <div className="px-4 py-3 flex items-center gap-3">
          <div className="flex-1">
            <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">Pickup</p>
            <p className="text-sm font-bold text-foreground font-mono">{load.pickupLocation}</p>
          </div>
          <span className="text-muted-foreground">&rarr;</span>
          <div className="flex-1">
            <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">Delivery</p>
            <p className="text-sm font-bold text-foreground font-mono">{load.deliveryLsd}</p>
          </div>
        </div>

        {/* Move job banner */}
        {load.moveJobName && (
          <div data-testid="rig-move-banner" className="mx-4 mb-3 px-3 py-2.5 rounded-lg bg-amber-500/10 border border-amber-500/20">
            <p className="text-xs font-semibold text-foreground">
              Part of rig move: {load.moveJobName}
            </p>
            <p className="text-[11px] text-muted-foreground mt-0.5">{rigMoveProgressLine}</p>
            {progressBar("bg-amber-500/20", "bg-amber-500")}
          </div>
        )}

        {masterBolLink}

        {/* Detail chips */}
        <div className="px-4 pb-3 grid grid-cols-2 gap-2">
          {[
            { label: "DEPARTURE", value: load.departureTime },
            { label: "DURATION", value: load.duration },
            { label: "CUSTOMER", value: load.customerName },
            { label: "LOAD TYPE", value: `${load.loadType} haul` },
          ].map((chip) => (
            <div key={chip.label} className="bg-card/60 rounded-lg px-3 py-2 border border-border/50">
              <p className="text-[9px] font-bold text-muted-foreground uppercase tracking-wider">{chip.label}</p>
              <p className="text-xs font-semibold text-foreground mt-0.5">{chip.value}</p>
            </div>
          ))}
        </div>

        {/* Cargo manifest */}
        <div className="px-4 pb-3 space-y-1.5">
          {load.cargo.map((c, i) => (
            <div key={i} className="flex items-center justify-between text-xs">
              <span className="text-foreground">{c.item}</span>
              <span className="font-mono font-medium text-foreground">{c.weight}</span>
            </div>
          ))}
        </div>

        {/* Special instructions */}
        {load.specialInstructions && (
          <div className="mx-4 mb-3 px-3 py-2 rounded-lg bg-warning/10 border border-warning/20">
            <p className="text-xs text-warning leading-relaxed">{load.specialInstructions}</p>
          </div>
        )}

        {/* Actions */}
        <div className="px-4 pb-4 space-y-2">
          <div className="flex gap-2">
            <Button
              variant="outline"
              disabled
              className="flex-1 h-12 text-sm font-semibold border-border opacity-50"
            >
              Decline
            </Button>
            <Button
              className="flex-1 h-12 text-sm font-semibold bg-primary text-primary-foreground"
              onClick={handleStartChecks}
            >
              Start pre-departure checks
            </Button>
          </div>
          <p className="text-[11px] text-muted-foreground text-center leading-relaxed">
            Accepting and declining a load aren&rsquo;t available yet &mdash; call
            dispatch to confirm or turn one down. Departure and arrival are
            recorded normally.
          </p>
        </div>
      </div>

      <BottomNav />
    </PhoneFrame>
  );

  /* -- Screen 3: Pre-departure Checklist -- */
  const ChecklistScreen = () => (
    <PhoneFrame statusBarTime={timeStr}>
      <AppHeader />

      {/* Title */}
      <div className="px-5 pb-3">
        <h1 className="text-lg font-bold text-foreground">Pre-departure{load.driverName ? `, ${load.driverName}` : ""}</h1>
        <p className="text-xs text-muted-foreground">Unit {load.truckId || "—"} · BOL #{load.bolNumber}</p>
      </div>

      {/* Checklist card */}
      <div className="mx-4 mb-4 bg-secondary rounded-xl border border-border overflow-hidden">
        {/* Accepted header */}
        <div className="px-4 py-2.5 flex items-center justify-between border-b border-border bg-success/10">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-success" />
            <span className="text-xs font-semibold text-success">Load accepted — {load.acceptedAt || timeStr}</span>
          </div>
          <span className="text-[11px] font-mono text-success">{load.bolNumber}</span>
        </div>

        {/* Dispatcher notified */}
        <div className="px-4 py-3 flex items-center gap-3 border-b border-border/50">
          <CheckCircle2 className="w-5 h-5 text-success shrink-0" />
          <div>
            <p className="text-xs font-semibold text-foreground">Dispatcher notified</p>
            <p className="text-[11px] text-muted-foreground">BOL generated · on the dispatch board</p>
          </div>
        </div>

        {/* Checklist */}
        <div className="px-4 py-3">
          <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-3">Pre-departure Checklist</p>
          <div className="space-y-2">
            {checklist.map((item) => (
              <button
                key={item.id}
                data-testid="predeparture-check"
                data-checked={item.checked ? "true" : undefined}
                onClick={() => toggleCheck(item.id)}
                className={`w-full flex items-center gap-3 p-3 rounded-lg text-left transition-colors min-h-[48px] ${
                  item.checked
                    ? "bg-success/10 border border-success/30"
                    : "bg-card/40 border border-border hover:border-muted-foreground"
                }`}
              >
                {item.checked ? (
                  <CheckCircle2 className="w-5 h-5 text-success shrink-0" />
                ) : (
                  <Circle className="w-5 h-5 text-muted-foreground shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <p className={`text-xs font-semibold ${item.checked ? "text-success" : "text-foreground"}`}>
                    {item.label}
                  </p>
                  <p className="text-[11px] text-muted-foreground">{item.description}</p>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Odometer input */}
        <div className="px-4 pb-3">
          <p className="text-[9px] font-bold text-muted-foreground uppercase tracking-wider mb-1">Odometer at departure ({distanceUnit})</p>
          <Input
            type="number"
            inputMode="numeric"
            placeholder="e.g. 123456"
            value={odometerAtDeparture}
            onChange={(e) => setOdometerAtDeparture(e.target.value)}
            className="bg-card/60 border-border text-xs h-11"
          />
        </div>

        {/* Depart button */}
        <div className="px-4 pb-4">
          <Button
            className={`w-full h-12 text-sm font-bold transition-all ${
              allChecked && odometerAtDeparture
                ? "bg-primary text-primary-foreground hover:bg-primary/90"
                : "bg-secondary text-muted-foreground cursor-not-allowed border border-border"
            }`}
            onClick={handleDepart}
            disabled={!allChecked || !odometerAtDeparture || departLoad.isPending}
          >
            {departLoad.isPending ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : null}
            {departLoad.isPending
              ? "Departing..."
              : !allChecked
              ? `${remainingChecks} check${remainingChecks !== 1 ? "s" : ""} remaining before departure`
              : !odometerAtDeparture
              ? "Enter odometer to depart"
              : "Depart \u2197"}
          </Button>
        </div>
      </div>

      <BottomNav />
    </PhoneFrame>
  );

  /* -- Screen 4: En Route -- */
  const EnRouteScreen = () => (
    <PhoneFrame statusBarTime={timeStr}>
      <AppHeader />

      {/* Title */}
      <div className="px-5 pb-3">
        <h1 className="text-lg font-bold text-foreground">En route{load.driverName ? `, ${load.driverName}` : ""}</h1>
        <p className="text-xs text-muted-foreground">Unit {load.truckId || "—"} · Departed {load.departedAt} · Arr. ~{load.estArrival}</p>
      </div>

      {/* En route card */}
      <div className="mx-4 mb-4 bg-secondary rounded-xl border border-border overflow-hidden">
        {/* En route header */}
        <div className="px-4 py-2.5 flex items-center justify-between border-b border-border bg-info/10">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-info" />
            <span className="text-xs font-semibold text-info">En route</span>
          </div>
          <span className="text-[11px] font-mono text-info">{load.bolNumber}</span>
        </div>

        {/* Progress */}
        <div className="px-4 py-3">
          <div className="flex items-center justify-between text-xs mb-1.5">
            <span className="font-medium text-foreground">{load.pickupLocation}</span>
            <span className="font-semibold text-info">{load.timeRemaining} left</span>
          </div>
          <div className="w-full h-2 rounded-full bg-border overflow-hidden">
            <div
              className="h-full rounded-full bg-info transition-all duration-1000"
              style={{ width: `${load.progressPercent || 0}%` }}
            />
          </div>
          <p className="text-[11px] text-muted-foreground mt-1.5">
            Departed {load.departedAt} · Est. arrival {load.estArrival}
          </p>
        </div>

        {/* Destination */}
        <div className="mx-4 mb-3 px-3 py-2.5 rounded-lg bg-info/10 border border-info/20">
          <p className="text-[9px] font-bold text-info uppercase tracking-wider">Delivery Destination</p>
          <p className="text-sm font-bold text-foreground font-mono mt-0.5">{load.deliveryLsd}</p>
          {load.deliveryDescription && (
            <p className="text-[11px] text-muted-foreground mt-0.5">{load.deliveryDescription}</p>
          )}
        </div>

        {/* Load info chips */}
        <div className="px-4 pb-3 grid grid-cols-2 gap-2">
          <div className="bg-card/60 rounded-lg px-3 py-2 border border-border/50">
            <p className="text-[9px] font-bold text-muted-foreground uppercase tracking-wider">Load Weight</p>
            <p className="text-xs font-semibold text-foreground mt-0.5">{load.totalWeight}</p>
          </div>
          <div className="bg-card/60 rounded-lg px-3 py-2 border border-border/50">
            <p className="text-[9px] font-bold text-muted-foreground uppercase tracking-wider">Load Type</p>
            <p className="text-xs font-semibold text-foreground mt-0.5">{load.loadType} haul</p>
          </div>
        </div>

        {/* Action buttons */}
        <div className="px-4 pb-3 grid grid-cols-2 gap-2">
          <Button variant="outline" className="h-12 text-xs font-semibold border-border gap-1.5" onClick={() => setShowDelayModal(true)}>
            <Flag className="w-3.5 h-3.5" /> Flag delay
          </Button>
          <Button variant="outline" className="h-12 text-xs font-semibold border-border gap-1.5" onClick={() => setShowFuelModal(true)}>
            <Fuel className="w-3.5 h-3.5" /> Log fuel stop
          </Button>
        </div>
        <div className="px-4 pb-4">
          <Button
            className="w-full h-12 text-sm font-bold bg-primary text-primary-foreground gap-2"
            onClick={handleArrival}
            disabled={arriveLoad.isPending}
          >
            {arriveLoad.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <MapPin className="w-4 h-4" />
            )}
            {arriveLoad.isPending ? "Confirming..." : "Confirm arrival at site \u2197"}
          </Button>
        </div>
      </div>

      {/* Today's loads */}
      {load.todayLoads && load.todayLoads.length > 0 && (
        <div className="mx-4 mb-4 bg-secondary/50 rounded-xl border border-border p-4">
          <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-2">Today's Loads</p>
          {load.todayLoads.map((tl, i) => (
            <div key={i} className="flex items-center justify-between py-1.5">
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${tl.status === "In progress" ? "bg-primary" : "bg-muted-foreground/40"}`} />
                <span className="text-xs text-foreground">{tl.label}</span>
              </div>
              <span className={`text-[11px] font-medium ${tl.status === "In progress" ? "text-primary" : "text-muted-foreground"}`}>
                {tl.status || tl.time}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Move job progress */}
      {load.moveJobName && (
        <div data-testid="rig-move-progress" className="mx-4 mb-4 bg-info/10 rounded-xl border border-info/20 p-4">
          <p className="text-xs font-semibold text-foreground">Rig move: {load.moveJobName}</p>
          <p className="text-[11px] text-muted-foreground mt-0.5">{rigMoveProgressLine}</p>
          {progressBar("bg-info/20", "bg-info")}
        </div>
      )}

      <BottomNav />
    </PhoneFrame>
  );

  /* ======================================================
     ARRIVAL SCREENS (5-8)
     ====================================================== */

  /* -- Screen 5: Arrive at Site -- */
  const ArrivalScreen = () => (
    <PhoneFrame statusBarTime={arrivalTime || timeStr}>
      <AppHeader />
      <div className="px-5 pb-3">
        <h1 className="text-lg font-bold text-foreground">Arrived at site</h1>
        <p className="text-xs text-muted-foreground">Unit {load.truckId || "—"} · {load.deliveryLsd} · {load.deliveryDescription?.split(" · ").pop()}</p>
      </div>

      <div className="mx-4 mb-4 bg-secondary rounded-xl border border-primary/30 overflow-hidden">
        <div className="px-4 py-2.5 flex items-center justify-between border-b border-border bg-primary/10">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-primary" />
            <span className="text-xs font-semibold text-primary">Confirm arrival</span>
          </div>
          <span className="text-[11px] font-mono text-primary">{load.bolNumber}</span>
        </div>

        {/* Destination */}
        <div className="px-4 py-3 flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary/15 flex items-center justify-center">
            <MapPin className="w-5 h-5 text-primary" />
          </div>
          <div>
            <p className="text-sm font-bold text-foreground">At destination</p>
            <p className="text-xs text-muted-foreground">{load.deliveryLsd} · {load.deliveryDescription}</p>
          </div>
        </div>

        {/* Detail chips */}
        <div className="px-4 pb-3 grid grid-cols-2 gap-2">
          <div className="bg-card/60 rounded-lg px-3 py-2 border border-border/50">
            <p className="text-[9px] font-bold text-muted-foreground uppercase tracking-wider">Departed</p>
            <p className="text-xs font-semibold text-foreground mt-0.5">{load.departedAt}</p>
          </div>
          <div className="bg-card/60 rounded-lg px-3 py-2 border border-border/50">
            <p className="text-[9px] font-bold text-muted-foreground uppercase tracking-wider">Drive Time</p>
            <p className="text-xs font-semibold text-foreground mt-0.5">{load.driveTimeMinutes != null ? formatMinutes(load.driveTimeMinutes) : "—"}</p>
          </div>
        </div>

        {/* Cargo summary */}
        <div className="px-4 pb-3">
          <div className="flex items-center justify-between text-xs mb-2">
            <span className="font-semibold text-foreground">Cargo on this load</span>
            <span className="font-mono text-muted-foreground">{load.cargo.length} items</span>
          </div>
          {load.cargo.map((c, i) => (
            <div key={i} className="flex items-center justify-between text-xs py-1 border-t border-border/30">
              <span className="text-foreground">{c.item}</span>
              <span className="font-mono font-medium text-foreground">{c.weight}</span>
            </div>
          ))}
        </div>

        {/* Log wait time */}
        <div className="px-4 pb-3">
          <Button
            variant="outline"
            className="w-full h-11 text-xs font-semibold border-border gap-1.5"
            onClick={() => {
              const nowIso = new Date().toISOString();
              // Both pickers prefill on the tenant's wall clock.
              setWaitEnd(toDatetimeLocal(nowIso, timeZone));
              // Pre-fill wait start with the arrival instant, or now
              setWaitStart(toDatetimeLocal(arrivedAtIso ?? nowIso, timeZone));
              setShowWaitModal(true);
            }}
          >
            <Clock className="w-3.5 h-3.5" /> Log wait time
          </Button>
          {loggedWaitMinutes !== null && (
            <div className="mt-2 px-3 py-2 rounded-lg bg-amber-500/10 border border-amber-500/20">
              <p className="text-xs font-semibold text-amber-400">
                Wait logged: {formatMinutes(loggedWaitMinutes)} — billing eligible
              </p>
            </div>
          )}
        </div>

        {/* Confirm button */}
        <div className="px-4 pb-4">
          <Button
            className="w-full h-12 text-sm font-bold bg-primary text-primary-foreground"
            onClick={() => setScreen("condition")}
          >
            Confirm arrival — {arrivalTime || timeStr}
          </Button>
        </div>
      </div>

      <BottomNav />
    </PhoneFrame>
  );

  /* -- Screen 6: Condition Check -- */
  const ConditionScreen = () => (
    <PhoneFrame statusBarTime={arrivalTime || timeStr}>
      <AppHeader />
      <div className="px-5 pb-3">
        <h1 className="text-lg font-bold text-foreground">Condition check</h1>
        <p className="text-xs text-muted-foreground">Unit {load.truckId || "—"} · {load.bolNumber} · Arrived {arrivalTime || timeStr}</p>
      </div>

      <div className="mx-4 mb-4 bg-secondary rounded-xl border border-border overflow-hidden">
        <div className="px-4 py-3 border-b border-border/50">
          <p className="text-sm font-bold text-foreground">Cargo condition on delivery</p>
          <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
            Inspect each item as it is offloaded. If any damage is observed, flag it here before the receiver signs.
          </p>
        </div>

        <div className="px-4 py-3 space-y-3">
          {cargoConditions.map((item, i) => (
            <div key={i} className="space-y-2">
              <div className="flex items-center justify-between text-xs">
                <span className="font-semibold text-foreground">{item.item}</span>
                <span className="font-mono text-muted-foreground">{item.weight}</span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => setConditionStatus(i, "ok")}
                  className={`h-11 rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5 border transition-colors ${
                    item.status === "ok"
                      ? "bg-success/15 border-success/40 text-success"
                      : "border-border text-muted-foreground hover:border-muted-foreground hover:text-foreground"
                  }`}
                >
                  {item.status === "ok" && <Check className="w-3.5 h-3.5" />}
                  No damage
                </button>
                <button
                  onClick={() => setConditionStatus(i, "damaged")}
                  className={`h-11 rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5 border transition-colors ${
                    item.status === "damaged"
                      ? "bg-destructive/15 border-destructive/40 text-destructive"
                      : "border-border text-muted-foreground hover:border-muted-foreground hover:text-foreground"
                  }`}
                >
                  {item.status === "damaged" && <AlertTriangle className="w-3.5 h-3.5" />}
                  Flag damage
                </button>
              </div>
              {item.status === "damaged" && (
                <Textarea
                  placeholder="Describe the damage observed..."
                  value={item.damageNote}
                  onChange={(e) => setDamageNote(i, e.target.value)}
                  className="bg-card/60 border-destructive/30 text-xs min-h-[60px] placeholder:text-muted-foreground"
                />
              )}
            </div>
          ))}
        </div>

        <div className="px-4 pb-4">
          <Button
            className={`w-full h-12 text-sm font-bold transition-all ${
              !allConditionsMarked
                ? "bg-secondary text-muted-foreground cursor-not-allowed border border-border"
                : hasDamage
                ? "bg-destructive text-destructive-foreground hover:bg-destructive/90"
                : "bg-primary text-primary-foreground hover:bg-primary/90"
            }`}
            onClick={handleConditionContinue}
            disabled={!allConditionsMarked}
          >
            {!allConditionsMarked
              ? "Mark all items to continue"
              : hasDamage
              ? "Continue with damage flagged"
              : "All clear — continue"}
          </Button>
        </div>
      </div>

      <BottomNav />
    </PhoneFrame>
  );

  /* -- Screen 7: Receiver Signature -- */
  const SignatureScreen = () => {
    const autoTimestamp = `${formatInstantDay(currentIso, timeZone, { weekday: "short", month: "short", day: "numeric" }) ?? "—"} · ${timeStr} · LSD ${load.deliveryLsd}`;
    const canSubmit = receiverName.trim().length > 0 && !!signatureData && !!odometerAtDelivery;

    return (
      <PhoneFrame statusBarTime={timeStr}>
        <AppHeader />
        <div className="px-5 pb-3">
          <h1 className="text-lg font-bold text-foreground">Receiver signature</h1>
          <p className="text-xs text-muted-foreground">Unit {load.truckId || "—"} · Hand phone to receiver</p>
        </div>

        <div className="mx-4 mb-4 bg-secondary rounded-xl border border-border overflow-hidden">
          <div className="px-4 py-2.5 flex items-center justify-between border-b border-border bg-primary/10">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-primary" />
              <span className="text-xs font-semibold text-primary">BOL sign-off</span>
            </div>
            <span className="text-[11px] font-medium text-muted-foreground">Hand to receiver</span>
          </div>

          <div className="px-4 py-3 border-b border-border/50">
            <p className="text-xs text-muted-foreground leading-relaxed">
              Hand your phone to the <span className="font-bold text-foreground">site receiver</span> to confirm delivery and sign the BOL below.
            </p>
          </div>

          {/* Delivery summary */}
          <div className="px-4 py-2.5 flex items-center justify-between text-xs border-b border-border/50">
            <span className="font-semibold text-foreground">Delivery confirmed — {load.cargo.length} items</span>
            <span className="font-mono text-foreground">{load.totalWeight}</span>
          </div>
          <div className="px-4 py-1.5 flex items-center justify-between text-xs border-b border-border/50">
            <span className="text-muted-foreground">All items</span>
            <span className={`font-medium ${hasDamage ? "text-destructive" : "text-success"}`}>
              {hasDamage ? "Damage reported" : "No damage reported"}
            </span>
          </div>

          {/* Name + Position */}
          <div className="px-4 pt-3 pb-2 grid grid-cols-2 gap-2">
            <div>
              <p className="text-[9px] font-bold text-muted-foreground uppercase tracking-wider mb-1">Receiver Name</p>
              <Input
                placeholder="Print name"
                value={receiverName}
                onChange={(e) => setReceiverName(e.target.value)}
                className="bg-card/60 border-border text-xs h-11"
              />
            </div>
            <div>
              <p className="text-[9px] font-bold text-muted-foreground uppercase tracking-wider mb-1">Position / Company</p>
              <Input
                placeholder="e.g. Tool push"
                value={receiverPosition}
                onChange={(e) => setReceiverPosition(e.target.value)}
                className="bg-card/60 border-border text-xs h-11"
              />
            </div>
          </div>

          {/* Odometer at delivery */}
          <div className="px-4 pb-2">
            <p className="text-[9px] font-bold text-muted-foreground uppercase tracking-wider mb-1">Odometer at delivery ({distanceUnit})</p>
            <Input
              type="number"
              inputMode="numeric"
              placeholder="e.g. 123789"
              value={odometerAtDelivery}
              onChange={(e) => setOdometerAtDelivery(e.target.value)}
              className="bg-card/60 border-border text-xs h-11"
            />
          </div>

          {/* Signature canvas */}
          <div className="px-4 py-2">
            <SignatureCanvas onSign={(data) => setSignatureData(data || null)} />
          </div>

          {/* Auto timestamp */}
          <div className="px-4 py-2 flex items-center gap-1.5">
            <div className="w-1.5 h-1.5 rounded-full bg-primary" />
            <p className="text-[10px] text-muted-foreground">Auto-timestamped: {autoTimestamp}</p>
          </div>

          {/* Submit */}
          <div className="px-4 pb-4">
            <Button
              className={`w-full h-12 text-sm font-bold transition-all ${
                canSubmit && !deliverLoad.isPending
                  ? "bg-primary text-primary-foreground hover:bg-primary/90"
                  : "bg-secondary text-muted-foreground cursor-not-allowed border border-border"
              }`}
              onClick={handleSignSubmit}
              disabled={!canSubmit || deliverLoad.isPending}
            >
              {deliverLoad.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : canSubmit ? (
                "Submit signed BOL"
              ) : (
                "Name, odometer, and signature required"
              )}
            </Button>
          </div>
        </div>

        <BottomNav />
      </PhoneFrame>
    );
  };

  /* -- Screen 8: Load Closed -- */
  const ClosedScreen = () => (
    <PhoneFrame statusBarTime={timeStr}>
      {/* Green header */}
      <div className="bg-success/15 px-5 py-4">
        <AppHeader />
        <div className="px-0 pb-1">
          <h1 className="text-lg font-bold text-foreground">Load complete</h1>
          <p className="text-xs text-muted-foreground">Unit {load.truckId || "—"} · BOL #{load.bolNumber} · Signed</p>
        </div>
      </div>

      <div className="px-6 py-6 text-center">
        <div className="w-14 h-14 rounded-full bg-success/20 flex items-center justify-center mx-auto mb-3">
          <CheckCircle2 className="w-8 h-8 text-success" />
        </div>
        <h2 className="text-base font-bold text-foreground">BOL signed — load closed</h2>
        <p className="text-xs text-muted-foreground mt-1">
          Receiver: {receiverName} · {receiverPosition}
        </p>
        <p className="text-xs text-muted-foreground">
          Dispatcher notified · Invoice draft triggered
        </p>
      </div>

      {/* Summary chips */}
      <div className="px-4 pb-3 grid grid-cols-2 gap-2">
        <div className="bg-secondary rounded-lg px-3 py-2 border border-border text-center">
          <p className="text-[9px] font-bold text-muted-foreground uppercase tracking-wider">BOL Ref</p>
          <p className="text-xs font-bold text-foreground font-mono mt-0.5">{load.bolNumber}</p>
        </div>
        <div className="bg-secondary rounded-lg px-3 py-2 border border-border text-center">
          <p className="text-[9px] font-bold text-muted-foreground uppercase tracking-wider">Signed At</p>
          <p className="text-xs font-bold text-foreground font-mono mt-0.5">{signedAt}</p>
        </div>
        <div className="bg-secondary rounded-lg px-3 py-2 border border-border text-center">
          <p className="text-[9px] font-bold text-muted-foreground uppercase tracking-wider">Drive Time</p>
          <p className="text-xs font-bold text-foreground font-mono mt-0.5">{load.driveTimeMinutes != null ? formatMinutes(load.driveTimeMinutes) : "—"}</p>
        </div>
        <div className="bg-secondary rounded-lg px-3 py-2 border border-border text-center">
          <p className="text-[9px] font-bold text-muted-foreground uppercase tracking-wider">Condition</p>
          <p className={`text-xs font-bold mt-0.5 ${hasDamage ? "text-destructive" : "text-success"}`}>
            {hasDamage ? "Damage noted" : "No damage"}
          </p>
        </div>
        {load.moveJobName && (
          <div className="bg-secondary rounded-lg px-3 py-2 border border-border text-center">
            <p className="text-[9px] font-bold text-muted-foreground uppercase tracking-wider">Move: loads signed</p>
            <p data-testid="rig-move-signed" className="text-xs font-bold text-foreground mt-0.5">
              {load.moveJobProgress
                ? `${count(load.moveJobProgress.signed)} / ${count(load.moveJobProgress.total)}`
                : "—"}
            </p>
          </div>
        )}
      </div>

      {masterBolLink}

      {/* Move job completion banner: the master BOL's own status, not a sum
        * made here. `complete` is the API's "every load signed". */}
      {load.moveJobName && load.moveJobProgress?.status === "complete" && (
          <div data-testid="rig-move-complete" className="mx-4 mb-3 px-3 py-2.5 rounded-lg bg-success/15 border border-success/30">
            <p className="text-xs font-semibold text-foreground text-center">
              Every load is signed. The master BOL is ready for the operator.
            </p>
          </div>
        )}

      {/* Action buttons */}
      <div className="px-4 space-y-2 pb-4">
        {/* The "View invoice draft" button that stood here is gone and nothing
          * replaces it. Its destination was a hardcoded literal ending in a BOL
          * reference that belongs to no load in any tenant, and
          * pages/InvoiceDraft.tsx -- the screen it opened -- made no API call and
          * ignored its own :bolId, rendering the same invented invoice for every
          * driver who pressed it. There is no invoice-for-this-load screen to
          * point at instead; a button that opens an invented invoice is worse
          * than no button. */}
        <Button
          variant="outline"
          className="w-full h-11 text-xs font-semibold border-border"
          disabled={!loadId}
          onClick={() => loadId && navigate(`/driver/bol/${loadId}`)}
        >
          View signed BOL PDF
        </Button>
        <Button
          className="w-full h-11 text-xs font-semibold bg-primary text-primary-foreground"
          onClick={() => navigate("/my-work/today")}
        >
          Next load
        </Button>
      </div>

      <BottomNav />
    </PhoneFrame>
  );

  const screenMap: Record<Screen, React.ReactNode> = {
    details: <DetailsScreen />,
    checklist: <ChecklistScreen />,
    enroute: <EnRouteScreen />,
    arrival: <ArrivalScreen />,
    condition: <ConditionScreen />,
    signature: <SignatureScreen />,
    closed: <ClosedScreen />,
  };

  return (
    <div className="min-h-full bg-background pb-[var(--bottom-chrome)]">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/95 backdrop-blur-sm border-b border-border px-4 py-3">
        <div className="flex items-center justify-between max-w-5xl mx-auto">
          <div className="flex items-center gap-3">
            <button onClick={goBack} className="p-1.5 rounded-lg hover:bg-secondary transition-colors">
              <ArrowLeft className="w-5 h-5 text-muted-foreground" />
            </button>
            <div>
              <h1 className="text-base font-bold text-foreground">{load.bolNumber ? `BOL ${load.bolNumber}` : "Load"}</h1>
              <p className="text-[11px] text-muted-foreground">{load.customerName || "—"} · {load.loadType}</p>
            </div>
          </div>
          <HosChip />
        </div>
      </header>

      {/* Screen tabs */}
      <div className="border-b border-border px-4 overflow-x-auto">
        <div className="flex items-center gap-1 max-w-5xl mx-auto py-2">
          {currentTabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setScreen(tab.key)}
              className={`px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap transition-colors min-h-[40px] ${
                screen === tab.key
                  ? "bg-primary/10 text-primary border border-primary/30"
                  : "text-muted-foreground hover:text-foreground hover:bg-secondary"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Screen content */}
      <div className="max-w-5xl mx-auto px-4 py-6">
        <div className="flex gap-8">
          {/* Phone preview */}
          <div className="flex-1 flex justify-center">
            {screenMap[screen]}
          </div>

          {/* Flow sidebar */}
          <div className="hidden lg:block w-72 shrink-0">
            {!isArrivalPhase ? (
              <>
                <h3 className="text-sm font-bold text-foreground mb-4">Driver flow</h3>
                <div className="space-y-0">
                  {[
                    { num: 1, label: "Dispatcher books load", desc: "BOL generated · truck assigned", done: true },
                    { num: 2, label: "Load details reviewed", desc: "Full manifest, route, special instructions", done: ["checklist", "enroute"].includes(screen), active: screen === "details" },
                    { num: 3, label: "Pre-departure", desc: "Checklist gates departure — tap items to check off", done: ["enroute"].includes(screen), active: screen === "checklist" },
                    { num: 4, label: "En route", desc: "Progress · fuel stops · delays", done: false, active: screen === "enroute" },
                    { num: 5, label: "Arrival + receiver signature", desc: "Receiver signs BOL on screen · load closes · invoice triggered", done: false },
                  ].map((stage, i) => (
                    <div key={stage.num} className="flex gap-3">
                      <div className="flex flex-col items-center">
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 ${
                          stage.done ? "bg-success/20 text-success" : stage.active ? "bg-primary/20 text-primary border border-primary/40" : "bg-secondary text-muted-foreground"
                        }`}>
                          {stage.done ? <CheckCircle2 className="w-3.5 h-3.5" /> : stage.num}
                        </div>
                        {i < 4 && <div className={`w-px h-8 ${stage.done ? "bg-success/30" : "bg-border"}`} />}
                      </div>
                      <div className="pb-4">
                        <p className={`text-xs font-semibold ${stage.done ? "text-success" : stage.active ? "text-primary" : "text-foreground"}`}>
                          {stage.label}
                        </p>
                        <p className="text-[11px] text-muted-foreground leading-relaxed mt-0.5">{stage.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <>
                <h3 className="text-sm font-bold text-foreground mb-4">Arrival flow</h3>
                <div className="space-y-0">
                  {[
                    { num: 1, label: "En route \u2192 arrival", desc: "Driver taps confirm arrival · timestamp locked", done: ["condition", "signature", "closed"].includes(screen), active: screen === "arrival" },
                    { num: 2, label: "Condition check", desc: "Each cargo item marked no damage or flagged · damage triggers a note", done: ["signature", "closed"].includes(screen), active: screen === "condition" },
                    { num: 3, label: "Receiver signature", desc: "Driver hands phone · receiver prints name + draws signature · auto-timestamped to LSD", done: screen === "closed", active: screen === "signature" },
                    { num: 4, label: "Load closed", desc: "BOL finalized · dispatcher notified · invoice draft triggered automatically", done: false, active: screen === "closed" },
                  ].map((stage, i) => (
                    <div key={stage.num} className="flex gap-3">
                      <div className="flex flex-col items-center">
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 ${
                          stage.done ? "bg-success/20 text-success" : stage.active ? "bg-primary/20 text-primary border border-primary/40" : "bg-secondary text-muted-foreground"
                        }`}>
                          {stage.done ? <CheckCircle2 className="w-3.5 h-3.5" /> : stage.num}
                        </div>
                        {i < 3 && <div className={`w-px h-8 ${stage.done ? "bg-success/30" : "bg-border"}`} />}
                      </div>
                      <div className="pb-4">
                        <p className={`text-xs font-semibold ${stage.done ? "text-success" : stage.active ? "text-primary" : "text-foreground"}`}>
                          {stage.label}
                        </p>
                        <p className="text-[11px] text-muted-foreground leading-relaxed mt-0.5">{stage.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* What happens in dispatch */}
                <div className="mt-6 bg-secondary/50 rounded-xl border border-border p-4">
                  <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-3">What happens in dispatch</p>
                  {[
                    { label: "Load status \u2192 Delivered", desc: "Dispatch board updates instantly" },
                    { label: "BOL PDF generated", desc: "With receiver signature embedded" },
                    { label: "Invoice draft created", desc: "From BOL data + customer price book" },
                    { label: "Dispatcher reviews + sends", desc: "One click to approve and push to ERP" },
                  ].map((action, i) => (
                    <div key={i} className="flex items-start gap-2.5 py-2">
                      <div className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${
                        screen === "closed" ? "bg-success/20" : "bg-secondary"
                      }`}>
                        {screen === "closed" ? (
                          <CheckCircle2 className="w-3 h-3 text-success" />
                        ) : (
                          <div className="w-2 h-2 rounded-full bg-muted-foreground/30" />
                        )}
                      </div>
                      <div>
                        <p className={`text-xs font-semibold ${screen === "closed" ? "text-success" : "text-foreground"}`}>{action.label}</p>
                        <p className="text-[11px] text-muted-foreground">{action.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* ═══ FUEL STOP MODAL ═══ */}
      <BottomSheet open={showFuelModal} onClose={() => setShowFuelModal(false)} title="Log fuel stop">
        <div className="space-y-3">
          <div>
            <p className="text-[9px] font-bold text-muted-foreground uppercase tracking-wider mb-1">Litres</p>
            <Input type="number" inputMode="decimal" placeholder="0" value={fuelLitres} onChange={(e) => setFuelLitres(e.target.value)} className="bg-secondary border-border h-11" />
          </div>
          <div>
            <p className="text-[9px] font-bold text-muted-foreground uppercase tracking-wider mb-1">Price per litre ($)</p>
            <Input type="number" inputMode="decimal" step="0.01" placeholder="0.00" value={fuelPricePerLitre} onChange={(e) => setFuelPricePerLitre(e.target.value)} className="bg-secondary border-border h-11" />
          </div>
          <div className="bg-secondary rounded-lg px-3 py-2.5 border border-border">
            <p className="text-[9px] font-bold text-muted-foreground uppercase tracking-wider">Total cost</p>
            <p className="text-sm font-bold text-foreground mt-0.5">
              ${((parseFloat(fuelLitres) || 0) * (parseFloat(fuelPricePerLitre) || 0)).toFixed(2)}
            </p>
          </div>
          <div>
            <p className="text-[9px] font-bold text-muted-foreground uppercase tracking-wider mb-1">Province</p>
            <select value={fuelProvince} onChange={(e) => setFuelProvince(e.target.value)} className="w-full bg-secondary border border-border rounded-lg px-3 py-2.5 text-sm text-foreground h-11">
              {["AB", "BC", "SK", "MB", "ON", "YT", "NT"].map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div>
            <p className="text-[9px] font-bold text-muted-foreground uppercase tracking-wider mb-1">Station / location</p>
            <Input placeholder="e.g. Petro-Canada Edson" value={fuelStation} onChange={(e) => setFuelStation(e.target.value)} className="bg-secondary border-border h-11" />
          </div>
          <div>
            <p className="text-[9px] font-bold text-muted-foreground uppercase tracking-wider mb-1">Odometer at fill ({distanceUnit})</p>
            <Input type="number" inputMode="numeric" placeholder="e.g. 123500" value={fuelOdometer} onChange={(e) => setFuelOdometer(e.target.value)} className="bg-secondary border-border h-11" />
          </div>
          <Button className="w-full h-12 text-sm font-bold bg-primary text-primary-foreground" onClick={handleFuelSubmit} disabled={fuelSubmitting}>
            {fuelSubmitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</> : "Save fuel stop"}
          </Button>
        </div>
      </BottomSheet>

      {/* ═══ FLAG DELAY MODAL ═══ */}
      <BottomSheet open={showDelayModal} onClose={() => setShowDelayModal(false)} title="Flag delay">
        <div className="space-y-3">
          <div>
            <p className="text-[9px] font-bold text-muted-foreground uppercase tracking-wider mb-1">Reason</p>
            <select value={delayReason} onChange={(e) => setDelayReason(e.target.value)} className="w-full bg-secondary border border-border rounded-lg px-3 py-2.5 text-sm text-foreground h-11">
              <option value="">Select reason...</option>
              {["Traffic", "Weather", "Mechanical", "Gate access", "Waiting at pickup", "Waiting at site", "Other"].map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          </div>
          <div>
            <p className="text-[9px] font-bold text-muted-foreground uppercase tracking-wider mb-1">Expected delay</p>
            <select value={delayMinutes} onChange={(e) => setDelayMinutes(Number(e.target.value))} className="w-full bg-secondary border border-border rounded-lg px-3 py-2.5 text-sm text-foreground h-11">
              <option value={15}>15 min</option>
              <option value={30}>30 min</option>
              <option value={60}>1 hour</option>
              <option value={120}>2+ hours</option>
            </select>
          </div>
          <div>
            <p className="text-[9px] font-bold text-muted-foreground uppercase tracking-wider mb-1">Notes (optional)</p>
            <Textarea placeholder="Additional details..." value={delayNotes} onChange={(e) => setDelayNotes(e.target.value)} className="bg-secondary border-border min-h-[70px]" />
          </div>
          <Button className="w-full h-12 text-sm font-bold bg-primary text-primary-foreground" onClick={handleDelaySubmit} disabled={delaySubmitting}>
            {delaySubmitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending...</> : "Notify dispatcher"}
          </Button>
        </div>
      </BottomSheet>

      {/* ═══ WAIT TIME MODAL ═══ */}
      <BottomSheet open={showWaitModal} onClose={() => setShowWaitModal(false)} title="Log wait time">
        <div className="space-y-3">
          <div>
            <p className="text-[9px] font-bold text-muted-foreground uppercase tracking-wider mb-1">Wait start</p>
            <Input type="datetime-local" value={waitStart} onChange={(e) => setWaitStart(e.target.value)} className="bg-secondary border-border h-11" />
          </div>
          <div>
            <p className="text-[9px] font-bold text-muted-foreground uppercase tracking-wider mb-1">Wait end</p>
            <Input type="datetime-local" value={waitEnd} onChange={(e) => setWaitEnd(e.target.value)} className="bg-secondary border-border h-11" />
          </div>
          <div className="bg-secondary rounded-lg px-3 py-2.5 border border-border">
            <p className="text-[9px] font-bold text-muted-foreground uppercase tracking-wider">Duration</p>
            <p className="text-sm font-bold text-foreground mt-0.5">{formatDuration(waitStart, waitEnd)}</p>
          </div>
          <div>
            <p className="text-[9px] font-bold text-muted-foreground uppercase tracking-wider mb-1">Reason</p>
            <select value={waitReason} onChange={(e) => setWaitReason(e.target.value)} className="w-full bg-secondary border border-border rounded-lg px-3 py-2.5 text-sm text-foreground h-11">
              <option value="">Select reason...</option>
              {["Waiting for rig crew", "Gate access delay", "Weather hold", "Equipment not ready", "Road conditions", "Other"].map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          </div>
          <div>
            <p className="text-[9px] font-bold text-muted-foreground uppercase tracking-wider mb-1">Notes (optional)</p>
            <Textarea placeholder="Additional details..." value={waitNotes} onChange={(e) => setWaitNotes(e.target.value)} className="bg-secondary border-border min-h-[70px]" />
          </div>
          <Button className="w-full h-12 text-sm font-bold bg-primary text-primary-foreground" onClick={handleWaitSubmit} disabled={waitSubmitting}>
            {waitSubmitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</> : "Log wait time"}
          </Button>
        </div>
      </BottomSheet>

    </div>
  );
};

export default DriverLoadView;
